import React, { useState, useEffect } from "react";
import {
  Store, ChefHat, Radio, Mail, Shield, Wrench,
} from "lucide-react";

/**
 * Phase 101fj (Feb 2026) — Table des matières collante sur `/parametres`.
 *
 * Alain : « Ranger Paramètres : Regroupe les quinze panneaux en cinq onglets
 * clairs avec descriptions courtes au dessus de chacun ». La page comporte
 * 35+ Cards empilées, difficile de s'y retrouver.
 *
 * Solution non-invasive : au lieu de casser le JSX en Tabs (haut risque),
 * on ajoute une nav sticky en haut avec 6 catégories qui scrollent vers
 * des ancres `id=""` posées sur les Cards. Effet quasi identique aux
 * onglets, zéro risque de régression.
 *
 * Chaque catégorie a une description courte explicitant à quoi elle sert.
 */

const CATEGORIES = [
  {
    id: "cat-shop",
    label: "Boutique",
    icon: Store,
    testid: "social-links-zone",
    color: "#8C6B2F",
    hint: "Photos, textes, pages éditoriales, zones de livraison, réseaux sociaux, clients B2C",
  },
  {
    id: "cat-production",
    label: "Production & Stock",
    icon: ChefHat,
    testid: "production-zone",
    color: "#1F4E3D",
    hint: "Impression étiquettes, consommables, ingrédients, liste de courses, planification production",
  },
  {
    id: "cat-nayax",
    label: "Ventes Nayax",
    icon: Radio,
    testid: "nayax-zone",
    color: "#B24A32",
    hint: "Cutoff sync global, mode expert Nayax, file d'arbitrage des ventes suspectes",
  },
  {
    id: "cat-emails",
    label: "Emails & Alertes",
    icon: Mail,
    testid: "invoicing-zone",
    color: "#6B4E71",
    hint: "Facturation QR-Bill, confirmations boutique, brief matinal, alertes stock 3x8",
  },
  {
    id: "cat-security",
    label: "Sécurité & API",
    icon: Shield,
    testid: "backup-zone",
    color: "#455A64",
    hint: "Comptes users, API keys M2M, TWINT, sauvegarde et restauration",
  },
  {
    id: "cat-maintenance",
    label: "Maintenance",
    icon: Wrench,
    testid: "maintenance-tools",
    color: "#8B0000",
    hint: "Outils de maintenance, corrections DLC/lots, audit ventes, zone de danger",
  },
];

export default function SettingsQuickNav() {
  const [activeCat, setActiveCat] = useState(null);

  useEffect(() => {
    const cats = CATEGORIES
      .map((c) => ({ c, el: document.querySelector(`[data-testid="${c.testid}"]`) }))
      .filter((x) => x.el);
    if (cats.length === 0) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        const first = entries.find((e) => e.isIntersecting);
        if (first) {
          const match = cats.find((x) => x.el === first.target);
          if (match) setActiveCat(match.c.testid);
        }
      },
      { rootMargin: "-100px 0px -60% 0px", threshold: 0 }
    );
    cats.forEach(({ el }) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const scrollTo = (testid) => {
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - 88;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <div
      className="sticky top-0 z-30 -mx-6 px-6 py-3 bg-[#FAFAF7]/95 backdrop-blur border-b border-[#E5E5E0]"
      data-testid="settings-quick-nav"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8C6B2F]">
            Paramètres · index
          </p>
          <p className="text-xs text-[#575A57] mt-0.5 truncate">
            Clique une catégorie pour aller à la section correspondante.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const isActive = activeCat === c.testid;
            return (
              <button
                key={c.id}
                onClick={() => scrollTo(c.testid)}
                data-testid={`settings-nav-${c.id}`}
                title={c.hint}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border transition-all rounded ${
                  isActive
                    ? "bg-white shadow-sm"
                    : "bg-white/40 hover:bg-white hover:shadow-sm"
                }`}
                style={{
                  borderColor: isActive ? c.color : "#E5E5E0",
                  color: c.color,
                }}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="font-medium">{c.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
